import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Alert, CircularProgress, Typography, Box,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import type { Study, OrderInfo } from '../../api/types';
import { api } from '../../api/rpc';

interface PaymentDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete: () => void;
}

export const PaymentDialog = ({ open, study, onClose, onComplete }: PaymentDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<OrderInfo | null>(null);

  const [paymentStatus, setPaymentStatus] = useState('UNPAID');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [externalId, setExternalId] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  useEffect(() => {
    if (!open || !study) return;
    setLoading(true);
    setError(null);

    api.getOrder(study.objKey)
      .then(oi => {
        setOrder(oi);
        if (oi?.detailOrder) {
          setPaymentStatus((oi.detailOrder.paymentStatus as string) ?? 'UNPAID');
          setAmount((oi.detailOrder.paymentAmount as string) ?? '');
          setPaymentMethod((oi.detailOrder.paymentMethod as string) ?? '');
          setExternalId((oi.detailOrder.paymentExternalId as string) ?? '');
          setPaymentNotes((oi.detailOrder.paymentNotes as string) ?? '');
        }
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load order'))
      .finally(() => setLoading(false));
  }, [open, study]);

  const handleSave = async () => {
    if (!study || !order) return;
    setSaving(true);
    setError(null);
    try {
      const updatedOrder: OrderInfo = {
        ...order,
        detailOrder: {
          ...order.detailOrder,
          paymentStatus,
          paymentAmount: amount,
          paymentMethod,
          paymentExternalId: externalId,
          paymentNotes,
        },
      };
      await api.updateInsurances(study.objKey, updatedOrder, study.patientKey);
      onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Payment</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {study && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>{study.name}</strong> — {study.acc} — {study.mod}
            </Typography>
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={paymentStatus}
                  label="Payment Status"
                  onChange={e => setPaymentStatus(e.target.value)}
                >
                  <MenuItem value="UNPAID">Unpaid</MenuItem>
                  <MenuItem value="PIF">Paid in Full</MenuItem>
                  <MenuItem value="POA">Payment on Account</MenuItem>
                  <MenuItem value="PARTIAL">Partial</MenuItem>
                  <MenuItem value="WAIVED">Waived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Amount"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                fullWidth size="small"
                type="number"
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentMethod}
                  label="Payment Method"
                  onChange={e => setPaymentMethod(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="CASH">Cash</MenuItem>
                  <MenuItem value="CHECK">Check</MenuItem>
                  <MenuItem value="CREDIT">Credit Card</MenuItem>
                  <MenuItem value="INSURANCE">Insurance</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="External Reference ID"
                value={externalId}
                onChange={e => setExternalId(e.target.value)}
                fullWidth size="small"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Payment Notes"
                value={paymentNotes}
                onChange={e => setPaymentNotes(e.target.value)}
                fullWidth multiline rows={2} size="small"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
